import { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Mail, CheckCircle, Loader2 } from 'lucide-react';
import { trackLead } from '@/lib/pinterest';

interface EmailSignupFormProps {
  heading?: string;
  subheading?: string;
  placeholder?: string;
  buttonLabel?: string;
  successMessage?: string;
  language?: 'en' | 'es';
  source?: string;
  variant?: 'section' | 'footer' | 'inline';
}

export default function EmailSignupForm({
  heading,
  subheading,
  placeholder,
  buttonLabel,
  successMessage,
  language = 'en',
  source = 'website',
  variant = 'section',
}: EmailSignupFormProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const isEs = language === 'es';

  const defaults = {
    heading: isEs ? '¡No te pierdas nada!' : 'Never Miss a Deal',
    subheading: isEs
      ? 'Suscríbete para recibir destinos exclusivos, ofertas especiales e inspiración de viaje directamente en tu bandeja de entrada.'
      : 'Subscribe for exclusive destinations, special offers, and travel inspiration delivered straight to your inbox.',
    placeholder: isEs ? 'Tu correo electrónico' : 'Your email address',
    buttonLabel: isEs ? 'Suscribirme' : 'Subscribe',
    successMessage: isEs
      ? '¡Gracias! Estás suscrito. Pronto recibirás inspiración de viaje.'
      : 'You\'re in! Expect travel inspiration in your inbox soon.',
  };

  const h = heading ?? defaults.heading;
  const sub = subheading ?? defaults.subheading;
  const ph = placeholder ?? defaults.placeholder;
  const btn = buttonLabel ?? defaults.buttonLabel;
  const success = successMessage ?? defaults.successMessage;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email) return;
    setStatus('loading');
    setErrorMsg('');

    try {
      const res = await fetch('/api/newsletter', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, name, language, source }),
      });
      const data = await res.json() as { ok?: boolean; error?: string };
      if (res.ok && data.ok) {
        setStatus('success');
        setEmail('');
        setName('');
        trackLead('Newsletter');
      } else {
        setStatus('error');
        setErrorMsg(data.error ?? (isEs ? 'Algo salió mal. Inténtalo de nuevo.' : 'Something went wrong. Please try again.'));
      }
    } catch {
      setStatus('error');
      setErrorMsg(isEs ? 'Algo salió mal. Inténtalo de nuevo.' : 'Something went wrong. Please try again.');
    }
  }

  if (variant === 'footer') {
    return (
      <div>
        <p className="text-white font-semibold text-sm mb-1">{h}</p>
        <p className="text-white/50 text-xs mb-3">{sub}</p>
        <AnimatePresence mode="wait">
          {status === 'success' ? (
            <motion.div
              key="success"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex items-center gap-2 text-[#00E5FF] text-sm"
            >
              <CheckCircle size={16} />
              <span>{success}</span>
            </motion.div>
          ) : (
            <motion.form
              key="form"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              onSubmit={handleSubmit}
              className="flex gap-2"
            >
              <label htmlFor="footer-email-input" className="sr-only">
                {ph}
              </label>
              <input
                id="footer-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={ph}
                required
                className="flex-1 min-w-0 bg-white/10 border border-white/15 rounded-lg px-3 py-2 text-white placeholder-white/30 text-xs focus:outline-none focus:border-[#00E5FF]/60 transition-colors"
              />
              <button
                type="submit"
                disabled={status === 'loading'}
                className="px-4 py-2 bg-[#00E5FF] text-[#0a1420] text-xs font-bold rounded-lg hover:bg-[#00c8e0] transition-colors disabled:opacity-60 shrink-0 flex items-center gap-1.5"
              >
                {status === 'loading' ? <Loader2 size={12} className="animate-spin" /> : btn}
              </button>
            </motion.form>
          )}
        </AnimatePresence>
        {status === 'error' && (
          <p className="text-red-400 text-xs mt-1.5">{errorMsg}</p>
        )}
      </div>
    );
  }

  // section / inline variant
  return (
    <section className="newsletter-section py-20 bg-muted relative overflow-hidden">

      <div className="container mx-auto px-4 lg:px-8 relative z-10">
        <motion.div
          className="max-w-2xl mx-auto text-center"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.6, ease: 'easeOut' as const }}
        >
          {/* Icon */}
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-secondary text-secondary-foreground mb-6" aria-hidden="true">
            <Mail size={24} />
          </div>

          <h2
            className="text-3xl md:text-5xl font-bold text-white mb-4 font-heading"
          >
            {h}
          </h2>
          <p className="text-white/60 text-lg leading-relaxed mb-10">
            {sub}
          </p>

          <AnimatePresence mode="wait">
            {status === 'success' ? (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="flex flex-col items-center gap-3"
              >
                <div className="w-16 h-16 rounded-full bg-[#00E5FF]/15 flex items-center justify-center">
                  <CheckCircle size={32} className="text-[#00E5FF]" />
                </div>
                <p className="text-white text-lg font-semibold">{success}</p>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                onSubmit={handleSubmit}
                className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto"
              >
                <label htmlFor="section-email-input" className="sr-only">
                  {ph}
                </label>
                <input
                  id="section-email-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={ph}
                  required
                  className="flex-1 bg-white/8 border border-white/15 rounded-xl px-5 py-4 text-white placeholder-white/35 text-sm focus:outline-none focus:border-[#00E5FF]/60 transition-colors"
                />
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="px-7 py-4 bg-[#00E5FF] text-[#0a1420] font-bold text-sm rounded-xl hover:bg-[#00c8e0] transition-all duration-200 hover:scale-105 disabled:opacity-60 disabled:scale-100 flex items-center justify-center gap-2 shrink-0"
                >
                  {status === 'loading' ? (
                    <><Loader2 size={16} className="animate-spin" /> {isEs ? 'Enviando...' : 'Sending...'}</>
                  ) : btn}
                </button>
              </motion.form>
            )}
          </AnimatePresence>

          {status === 'error' && (
            <p className="text-red-400 text-sm mt-3">{errorMsg}</p>
          )}

          {status !== 'success' && (
            <p className="text-white/25 text-xs mt-4">
              {isEs ? 'Sin spam. Cancela cuando quieras.' : 'No spam. Unsubscribe anytime.'}
            </p>
          )}
        </motion.div>
      </div>
    </section>
  );
}
